import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, setSessionToken } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { useAuth } from "@/context/AuthContext";

// Module-level dedupe. React.StrictMode double-mounts in dev (and some edge
// cases in prod too) which would otherwise cause us to POST /auth/session
// twice with the same one-shot `session_id` — the second call ALWAYS 401s
// because Emergent has already consumed the session, and the user ends up
// looping to /login. A useRef guard resets on remount, so we pin the in-
// flight promise at the module scope. `sessionStorage` persists across full
// page remounts inside the same tab for belt-and-braces coverage.
let inFlight = null;

const seen = (id) => {
  try { return sessionStorage.getItem("oak_seen_session_id") === id; } catch { return false; }
};
const markSeen = (id) => {
  try { sessionStorage.setItem("oak_seen_session_id", id); } catch {}
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, markSessionFetched } = useAuth();

  useEffect(() => {
    const frag = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(frag);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      markSessionFetched();
      navigate("/", { replace: true });
      return;
    }

    // Already exchanged this exact session_id in this tab? Skip the POST and
    // just honour whatever state AuthContext already has.
    if (seen(sessionId)) {
      window.history.replaceState({}, "", window.location.pathname);
      markSessionFetched();
      const ok = localStorage.getItem("oak_is_logged_in") === "1";
      navigate(ok ? "/" : "/login", { replace: true });
      return;
    }

    // StrictMode firing the effect a second time? Re-use the original
    // promise — do NOT kick off a second request with the same session_id.
    const exchange = inFlight || (inFlight = (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (data?.session_token) setSessionToken(data.session_token);
        markSeen(sessionId);
        return { ok: true, user: data?.user || null };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[oak] /auth/session exchange failed:", err?.response?.data || err?.message);
        return { ok: false };
      } finally {
        // Clear the in-flight handle in the next microtask so a later
        // independent sign-in isn't blocked. `seen()` covers same-id replays.
        setTimeout(() => { inFlight = null; }, 0);
      }
    })());

    exchange.then((result) => {
      if (result.ok && result.user) setUser(result.user);
      else if (!result.ok) {
        setSessionToken(null);
        setUser(null);
      }
      window.history.replaceState({}, "", window.location.pathname);
      markSessionFetched();
      navigate(result.ok ? "/" : "/login", { replace: true });
    });
  }, [navigate, setUser, markSessionFetched]);

  return <LoadingScreen label="Signing you in…" />;
}
